import type { NodeSelection, Selection } from '@tiptap/pm/state';
import type {
  IRicosEditor,
  IsVisible,
  PluginToolbar,
  PluginToolbarButton,
  PluginToolbarConfig,
  ResolveReferenceElement,
  RicosPluginToolbarNamesValues,
  RicosServices,
  ToolbarButtonRenderers,
} from 'ricos-types';

import { RicosPluginToolbarButton } from './pluginToolbarButton';

export class PluginToolbarButtonCollisionError extends Error {}

export class RicosPluginToolbar implements PluginToolbar {
  buttons: PluginToolbarButton[];

  pluginType: RicosPluginToolbarNamesValues;

  customIsVisible?: IsVisible;

  customResolveReferenceElement?: ResolveReferenceElement;

  constructor(
    toolbar: PluginToolbarConfig,
    pluginType: RicosPluginToolbarNamesValues,
    services: RicosServices,
    config: Record<string, any>
  ) {
    this.buttons =
      toolbar
        ?.getButtons(config, services)
        ?.map(button => RicosPluginToolbarButton.of(button, services)) || [];
    this.pluginType = pluginType;
    this.customIsVisible = toolbar.isVisible;
    this.customResolveReferenceElement = toolbar.resolveReferenceElement;
  }

  static of(
    toolbar: PluginToolbarConfig,
    pluginType: RicosPluginToolbarNamesValues,
    services: RicosServices,
    config: Record<string, any>
  ) {
    return new RicosPluginToolbar(toolbar, pluginType, services, config);
  }

  register() {
    this.buttons.map(b => b.register());
  }

  unregister() {
    this.buttons.map(b => b.unregister());
  }

  getButtons() {
    return this.buttons;
  }

  toToolbarItemsConfig() {
    return this.buttons.map(button => button.toToolbarItemConfig());
  }

  getToolbarButtonsRenderers() {
    return this.buttons.reduce((renderers, button) => {
      return {
        ...renderers,
        ...button.getRenderer(),
      };
    }, {} as ToolbarButtonRenderers);
  }

  isVisible(selection: Selection | NodeSelection, editor: IRicosEditor) {
    const isVisibleInSelection =
      'node' in selection && selection.node?.type.name === this.pluginType;

    return this.customIsVisible
      ? isVisibleInSelection || this.customIsVisible(selection as Selection, editor)
      : isVisibleInSelection;
  }

  getReferenceElementResolver() {
    return this.customResolveReferenceElement;
  }
}
